/**
 * useWebVoice — voice IN (streaming) + voice OUT for the Ollie web surface.
 *
 * ARCHITECTURE_LOCKED.md chunk A2: the third surface gets the same voice
 * stack Mako and Orca already shipped.
 *
 *  - speakText(text): POST /api/audio/tts → sequential playback of the
 *    returned MP3 segments (Mako's chunked-voice-note UX). Cancelable.
 *  - startStreaming({onInterim, onFinal}): mic → MediaRecorder
 *    (webm/opus) → Clerk-verified WebSocket proxy at /api/audio/stream →
 *    Deepgram Nova-3 (Orca's streaming pattern; the master Deepgram key
 *    never reaches the browser — this account's key can't mint
 *    ephemeral tokens, so the server proxies the stream).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { apiUrl } from "../config/api";
import { authFetch } from "../utils/api";

export function useWebVoice() {
  const { getToken } = useAuth();
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);

  const audioRef = useRef(null); // current HTMLAudioElement
  const cancelSpeakRef = useRef(false);
  const wsRef = useRef(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);

  // ── Voice OUT ────────────────────────────────────────────────────────

  const stopSpeaking = useCallback(() => {
    cancelSpeakRef.current = true;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsSpeaking(false);
  }, []);

  const speakText = useCallback(
    async (text) => {
      if (!text || !text.trim()) return { spoken: false };
      stopSpeaking();
      cancelSpeakRef.current = false;

      const resp = await authFetch(apiUrl("/api/audio/tts"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!resp.ok) {
        throw new Error(`TTS request failed (${resp.status})`);
      }
      const { segments, mimeType } = await resp.json();
      if (!segments?.length) return { spoken: false };

      setIsSpeaking(true);
      try {
        for (const b64 of segments) {
          if (cancelSpeakRef.current) break;
          await playBase64Audio(b64, mimeType || "audio/mpeg", audioRef);
        }
      } finally {
        setIsSpeaking(false);
        audioRef.current = null;
      }
      return { spoken: !cancelSpeakRef.current, segments: segments.length };
    },
    [stopSpeaking],
  );

  // ── Voice IN (streaming) ─────────────────────────────────────────────

  const stopStreaming = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    recorderRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (wsRef.current) {
      try {
        // Tell Deepgram we're done so it flushes the final transcript.
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: "CloseStream" }));
        }
        wsRef.current.close();
      } catch (_) {
        /* already closed */
      }
      wsRef.current = null;
    }
    setIsStreaming(false);
  }, []);

  const startStreaming = useCallback(
    async ({ onInterim, onFinal, onError } = {}) => {
      stopStreaming();

      // 1. Fresh Clerk session token — rides the ws URL as a query
      //    param (browser WebSocket can't set headers); the server
      //    proxy verifies it before piping to Deepgram. The Deepgram
      //    key itself never reaches the browser.
      const clerkToken = await getToken({ skipCache: true });
      if (!clerkToken) throw new Error("not signed in");

      // 2. Mic
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // 3. WebSocket to OUR proxy (server/services/sttStreamProxy.js)
      const wsBase = apiUrl("/api/audio/stream").replace(/^http/, "ws");
      const ws = new WebSocket(`${wsBase}?token=${encodeURIComponent(clerkToken)}`);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type !== "Results") return;
          const alt = msg.channel?.alternatives?.[0];
          const transcript = alt?.transcript || "";
          if (!transcript) return;
          if (msg.is_final) {
            onFinal?.(transcript);
          } else {
            onInterim?.(transcript);
          }
        } catch (_) {
          /* non-JSON frame — ignore */
        }
      };
      ws.onerror = () => {
        onError?.(new Error("Deepgram stream error"));
        stopStreaming();
      };
      ws.onclose = () => setIsStreaming(false);

      await new Promise((resolve, reject) => {
        ws.onopen = resolve;
        const t = setTimeout(() => reject(new Error("Deepgram WS timeout")), 8000);
        ws.addEventListener("open", () => clearTimeout(t), { once: true });
      });

      // 4. Stream mic chunks (250ms cadence, containerized webm/opus —
      //    Deepgram auto-detects the container, no encoding params needed)
      const recorder = new MediaRecorder(stream, { mimeType: pickMimeType() });
      recorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0 && ws.readyState === WebSocket.OPEN) {
          ws.send(e.data);
        }
      };
      recorder.start(250);
      setIsStreaming(true);
    },
    [stopStreaming, getToken],
  );

  // Cleanup on unmount
  useEffect(() => () => {
    stopSpeaking();
    stopStreaming();
  }, []);

  return {
    speakText,
    stopSpeaking,
    isSpeaking,
    startStreaming,
    stopStreaming,
    isStreaming,
  };
}

function pickMimeType() {
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
  for (const c of candidates) {
    if (window.MediaRecorder && MediaRecorder.isTypeSupported(c)) return c;
  }
  return "";
}

function playBase64Audio(b64, mimeType, audioRef) {
  return new Promise((resolve, reject) => {
    const audio = new Audio(`data:${mimeType};base64,${b64}`);
    audioRef.current = audio;
    audio.onended = resolve;
    audio.onerror = () => reject(new Error("audio playback failed"));
    audio.play().catch(reject);
  });
}

export default useWebVoice;
